import { useAppTranslation } from '@/hooks/useAppTranslation';
import { useAuth } from '@/hooks/useAuth';
import { useOfflineDegradedMode } from '@/hooks/useOfflineDegradedMode';
import type { StandardHttpErrorDto } from '@api/WhisApi/generated/model/standardHttpErrorDto';
import { getUsersGetMeQueryKey } from '@api/WhisApi/generated/users/users';
import { Button } from '@components/Button/Button';
import { LoadingView } from '@components/LoadingView/LoadingView';
import { useQueryClient } from '@tanstack/react-query';
import {
  type Href,
  useGlobalSearchParams,
  usePathname,
  useRootNavigationState,
  useRouter,
  useSegments,
} from 'expo-router';
import { ReactNode, useEffect, useRef } from 'react';
import { H1, SizableText, YStack } from 'tamagui';

type AuthGateProps = {
  children: ReactNode;
};

export function AuthGate({ children }: AuthGateProps): ReactNode {
  const {
    status,
    isAuthenticated,
    userFetchStatus,
    userFetchError,
    logout,
    isReconnectingSession,
  } = useAuth();
  const {
    isOfflineDegradedMode,
    canUseOfflineReportCreation,
    isOfflineModeResolving,
  } = useOfflineDegradedMode();
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();

  const segments = useSegments();
  const pathname = usePathname();
  const { redirectTo } = useGlobalSearchParams<{ redirectTo?: string }>();
  const router = useRouter();
  const navigationState = useRootNavigationState();

  /**
   * Prevent multiple redirects during the same auth transition.
   * This is REQUIRED to avoid infinite navigation loops.
   */
  const hasRedirectedRef = useRef(false);

  const handleUnauthorizedRetry = async (
    error: StandardHttpErrorDto | null | undefined,
  ): Promise<void> => {
    if (error?.statusCode !== 401) return;

    // Ensure tokens are cleared before redirecting back to login.
    await logout();
    hasRedirectedRef.current = true;
    router.replace('/(auth)/login');
  };

  const reloadCurrentUser = async (): Promise<void> => {
    const queryKey = getUsersGetMeQueryKey();

    await queryClient.refetchQueries({
      queryKey,
      type: 'active',
    });

    const queryState = queryClient.getQueryState(queryKey);
    await handleUnauthorizedRetry(
      queryState?.error as StandardHttpErrorDto | undefined,
    );
  };

  useEffect(() => {
    if (!navigationState?.key) return;
    if (status === 'loading') return;

    const isAuthRoute = segments[0] === '(auth)';
    const ignoredRedirectTargets = [
      '/',
      '/expo-auth-session',
      '/redirect',
      '/+not-found',
      '/login',
    ];
    const shouldIgnoreCurrentPathForRedirect = ignoredRedirectTargets.includes(
      pathname,
    );
    const safeRedirectTarget =
      typeof redirectTo === 'string' &&
      redirectTo.startsWith('/') &&
      !ignoredRedirectTargets.includes(redirectTo) &&
      !redirectTo.startsWith('/(auth)') &&
      redirectTo !== pathname
        ? redirectTo
        : null;
    const isReportCreationRoute = pathname.startsWith(
      '/(tabs)/report-creation',
    );

    // Reset guard when auth state changes
    hasRedirectedRef.current = false;

    if (isOfflineModeResolving) {
      return;
    }

    if (isOfflineDegradedMode) {
      if (isAuthRoute) {
        if (hasRedirectedRef.current) return;
        hasRedirectedRef.current = true;
        router.replace('/(tabs)/report-creation');
      }
      return;
    }

    if (
      !isAuthenticated &&
      canUseOfflineReportCreation &&
      isReportCreationRoute
    ) {
      return;
    }

    if (!isAuthenticated && !isAuthRoute) {
      if (hasRedirectedRef.current) return;
      hasRedirectedRef.current = true;

      router.replace(
        shouldIgnoreCurrentPathForRedirect
          ? '/(auth)/login'
          : {
              pathname: '/(auth)/login',
              params: { redirectTo: pathname },
            },
      );
      return;
    }

    if (isAuthenticated && isAuthRoute) {
      if (hasRedirectedRef.current) return;
      hasRedirectedRef.current = true;

      router.replace(
        safeRedirectTarget ? (safeRedirectTarget as Href) : '/(tabs)/board',
      );
      return;
    }
  }, [
    isAuthenticated,
    isOfflineDegradedMode,
    canUseOfflineReportCreation,
    isOfflineModeResolving,
    status,
    navigationState?.key,
    segments,
    pathname,
    redirectTo,
    router,
  ]);

  const isUserInitializing =
    isAuthenticated &&
    (userFetchStatus === 'idle' || userFetchStatus === 'loading');

  if (
    status === 'loading' ||
    isUserInitializing ||
    isOfflineModeResolving ||
    isReconnectingSession
  ) {
    return <LoadingView message={t('auth.loadingProfile')} />;
  }

  if (userFetchStatus === 'error') {
    return (
      <YStack
        flex={1}
        alignItems="center"
        justifyContent="center"
        gap="$6"
        padding="$true"
      >
        <YStack>
          <H1 textAlign="center">{t('auth.userLoadError.title')}</H1>
          <SizableText size="$1" color="$secondary50" textAlign="center">
            {t('auth.userLoadError.description')}
          </SizableText>
        </YStack>
        <YStack gap="$2">
          <Button
            title={t('auth.retry')}
            variant="primary"
            onPress={reloadCurrentUser}
          />
          {userFetchError instanceof Error && (
            <SizableText size="$2" color="$errorFont" textAlign="center">
              {userFetchError.message}
            </SizableText>
          )}
        </YStack>
      </YStack>
    );
  }

  return <>{children}</>;
}
